import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Hero from '../common/Hero';
import MemberCard from '../features/MemberCard';
import DancingShield from "../common/DancingShield";

const RegistryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Section = styled.section`
  margin: 4rem 0;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: #333;
  font-family: 'Playfair Display', serif;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: #6c5ce7;
  }
`;

const Introduction = styled.div`
  margin-bottom: 3rem;
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #555;
  }
`;

const SearchAndFilter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #6c5ce7;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #6c5ce7;
  }
`;

const MembersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
  font-size: 1.1rem;
`;

const MemberRegistry = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [branchFilter, setBranchFilter] = useState('all');
    const [filteredMembers, setFilteredMembers] = useState([]);

    // Sample family members data
    const familyMembers = [
        {
            id: 1,
            name: 'Pasca Dumani',
            age: 'Unspecified',
            branch: 'Main',
            location: 'Zimbabwe',
            occupation: 'Mlalazi International Chairperson',
            bio: 'Mlalazi International Chairperson',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/pasca.jpg',
            email: 'pasca@mlalazi.family',
            phone: '+263 77 570 6709'
        },
        {
            id: 2,
            name: 'Reginah Mlalazi',
            age: 'Unspecified',
            branch: 'Main',
            location: 'Zimbabwe',
            occupation: 'Mlalazi Newsletter Overseer',
            bio: 'Mlalazi Newsletter Overseer',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/pasca.jpg',
            email: 'reginah@mlalazi.family',
            phone: '+263 77 380 5927'
        },
        {
            id: 3,
            name: 'Naidine Sibanda',
            age: 'Unspecified',
            branch: 'Johannesburg',
            location: 'JHB, South Africa',
            occupation: 'Mlalazi Newsletter Editor',
            bio: 'Mlalazi Newsletter Editor',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/naidine.jpg',
            email: 'naidine@mlalazi.family',
            phone: '+27 71 059 0527'
        },
        {
            id: 4,
            name: 'Msizi Dumani',
            age: 'Unspecified',
            branch: 'Harare',
            location: 'Brussels, Belgium',
            occupation: 'Mlalazi Newsletter Designer',
            bio: 'Mlalazi Newsletter Designer',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/msizi.png',
            email: 'msizi@mlalazi.family',
            phone: '+32 498 06 05 28'
        },
        {
            id: 5,
            name: 'Bongani Mlalazi',
            age: 'Unspecified',
            branch: 'Main',
            location: 'Zimbabwe',
            occupation: 'Mlalazi Newsletter IT Assistant',
            bio: 'Mlalazi Newsletter IT Assistant',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/bongani.jpg',
            email: 'bongani@mlalazi.family',
            phone: '+263 78 313 2230'
        },
        {
            id: 6,
            name: 'Winnie Mlalazi',
            age: 'Unspecified',
            branch: 'Main',
            location: 'Zimbabwe',
            occupation: 'Mlalazi Newsletter IT Assistant',
            bio: 'Mlalazi Newsletter IT Assistant',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/winnie.jpg',
            email: 'winnie@mlalazi.family',
            phone: '+27 72 092 3613'
        },
        {
            id: 7,
            name: 'Alself Mlalazi',
            age: 'Unspecified',
            branch: 'Main',
            location: 'Zimbabwe',
            occupation: 'Mlalazi Newsletter Graphic Designer',
            bio: 'Mlalazi Newsletter Graphic Designer',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/alself.jpg',
            email: 'alself@mlalazi.family',
            phone: 'Unspecified'
        },
        {
            id: 8,
            name: 'Lindani Mlalazi',
            age: 'Unspecified',
            branch: 'Johannesburg',
            location: 'JHB, South Africa',
            occupation: 'Mlalazi Newsletter Reporter',
            bio: 'Mlalazi Newsletter Reporter',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/lindani.jpg',
            email: 'lindani@mlalazi.family',
            phone: 'Unspecified'
        },
        {
            id: 9,
            name: 'Sibonginkosi Mlalazi',
            age: 'Unspecified',
            branch: 'Harare',
            location: 'Harare, Zimbabwe',
            occupation: 'Mlalazi Newsletter Reporter',
            bio: 'Mlalazi Newsletter Reporter',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/sibonginkosi.jpg',
            email: 'sibonginkosi@mlalazi.family',
            phone: 'Unspecified'
        },
        {
            id: 10,
            name: 'Faith Mlalazi',
            age: 'Unspecified',
            branch: 'Midlands',
            location: 'Gweru, Zimbabwe',
            occupation: 'Mlalazi Newsletter Reporter',
            bio: 'Mlalazi Newsletter Reporter',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/faith.jpg',
            email: 'faith@mlalazi.family',
            phone: 'Unspecified'
        },
        {
            id: 11,
            name: 'Octavia Dumane',
            age: 'Unspecified',
            branch: 'Botswana',
            location: 'Botswana',
            occupation: 'Mlalazi Newsletter Reporter',
            bio: 'Mlalazi Newsletter Reporter',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/octavia.jpg',
            email: 'octavia@mlalazi.family',
            phone: '+267 74 268 065'
        },
        {
            id: 12,
            name: 'Sisasenkosi Dumane',
            age: 'Unspecified',
            branch: 'Plumtree',
            location: 'Plumtree, Zimbabwe',
            occupation: 'Mlalazi Newsletter Reporter',
            bio: 'Mlalazi Newsletter Reporter',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/sisasenkosi.jpg',
            email: 'sisasenkosi@mlalazi.family',
            phone: 'Unspecified'
        },
        {
            id: 13,
            name: 'Immaculate Dumane',
            age: 'Unspecified',
            branch: 'Bulawayo',
            location: 'Bulawayo, Zimbabwe',
            occupation: 'Mlalazi Newsletter Reporter',
            bio: 'Mlalazi Newsletter Reporter',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/immaculate.jpg',
            email: 'immaculate@mlalazi.family',
            phone: 'Unspecified'
        }
    ];

    // Get unique branches for filter dropdown
    const branches = ['all', ...new Set(familyMembers.map(member => member.branch))];

    // Filter members based on search term and branch filter
    useEffect(() => {
        let results = familyMembers;

        if (searchTerm) {
            results = results.filter(member =>
                member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.occupation.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.location.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (branchFilter !== 'all') {
            results = results.filter(member => member.branch === branchFilter);
        }

        setFilteredMembers(results);
    }, [searchTerm, branchFilter]);

    return (
        <>
            <Hero
                title="Clan Registry"
                subtitle="Connecting our clan across generations and geographies"
                backgroundImage="/assets/images/logo.jpg"
            />

            <RegistryContainer>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <Introduction>
                        <SectionTitle>Our Clan Members</SectionTitle>
                        <p>
                            Our clan registry helps us stay connected across generations and geographies. Here you can find
                            contact information for clan members, learn about their lives and accomplishments, and discover
                            how we're all connected. This registry is regularly updated to include new members and keep
                            information current.
                        </p>
                    </Introduction>

                    <Section>
                        <SearchAndFilter>
                            <SearchInput
                                type="text"
                                placeholder="Search by name, occupation, or location..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <FilterSelect
                                value={branchFilter}
                                onChange={(e) => setBranchFilter(e.target.value)}
                            >
                                {branches.map(branch => (
                                    <option key={branch} value={branch}>
                                        {branch === 'all' ? 'All Branches' : `${branch} Branch`}
                                    </option>
                                ))}
                            </FilterSelect>
                        </SearchAndFilter>

                        {filteredMembers.length > 0 ? (
                            <MembersGrid>
                                {filteredMembers.map(member => (
                                    <MemberCard
                                        key={member.id}
                                        member={member}
                                    />
                                ))}
                            </MembersGrid>
                        ) : (
                            <NoResults>
                                <DancingShield />
                                <p>No clan members found matching your search criteria.</p>
                            </NoResults>
                        )}
                    </Section>
                </motion.div>
            </RegistryContainer>
        </>
    );
};

export default MemberRegistry;